import {type DiscordSDK} from '@discord/embedded-app-sdk'

type ConsoleLogLevel = Parameters<DiscordSDK['commands']['captureLog']>[0]['level']

export class DiscordMountConsoleCapture {
    private readonly discordSDK: DiscordSDK

    constructor(discordSDK: DiscordSDK) {
        this.discordSDK = discordSDK

        const iframe = document.getElementById('content-frame') as HTMLIFrameElement
        if(iframe === null) return

        const isIframeAlreadyLoaded = iframe.contentWindow !== null
        if(isIframeAlreadyLoaded) {
            this.initFrameConsoleCapture(iframe)
        } else {
            iframe.addEventListener('load', () => {
                this.initFrameConsoleCapture(iframe)
            })
        }
    }

    private initFrameConsoleCapture(frame: HTMLIFrameElement) {
        try {
            const frameWindow = frame.contentWindow as (Window & typeof globalThis) | null
            if(frameWindow === null) return

            const frameConsole = frameWindow.console
            const levels: ConsoleLogLevel[] = ['log', 'debug', 'info', 'warn', 'error']
            for(const level of levels) {
                const originalMethod = frameConsole[level].bind(frameConsole)
                frameConsole[level] = (...args: unknown[]) => {
                    originalMethod(...args)
                    this.forwardLogToDiscord(level, args)
                }
            }

            // Also capture uncaught errors from the iframe
            frameWindow.addEventListener('error', (event) => {
                this.forwardLogToDiscord('error', [`Uncaught Error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno}`])
            })

            frameWindow.addEventListener('unhandledrejection', (event) => {
                this.forwardLogToDiscord('error', [`Unhandled Promise Rejection: ${event.reason}`])
            })
        } catch(err) {
            // Cross-origin or other access error - silently fail
        }
    }

    private forwardLogToDiscord(level: ConsoleLogLevel, args: unknown[]) {
        try {
            const message = args.map((arg) => {
                if(arg === undefined) return 'undefined'
                if(arg === null) return 'null'
                if(typeof arg === 'object') {
                    try {
                        return JSON.stringify(arg)
                    } catch{
                        return String(arg)
                    }
                }
                return String(arg)
            }).join(' ')

            void this.discordSDK.commands.captureLog({level: level, message: message})
        } catch(err) {
            // Silently fail - logging should not break the app
        }
    }
}
